import { useMemo } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import { Fonts } from "@/constants/theme";
import { monthLabel } from "@/lib/month-label";
import type { MonthlyVibeCardContent } from "@/types";

export const MONTHLY_VIBE_CARD_SIZE = 1080;

type TemplateProps = {
  content: MonthlyVibeCardContent;
  monthText: string;
  brandLabel: string;
  t: (key: string) => string;
};

export function MonthlyVibeCardRenderer({
  content,
}: {
  content: MonthlyVibeCardContent;
}) {
  const { t, i18n } = useTranslation();
  const props: TemplateProps = {
    content,
    monthText: monthLabel(content.periodKey, i18n.language),
    brandLabel: t("profile.monthlyVibeCard.branding.madeWith"),
    t: t as unknown as (key: string) => string,
  };

  switch (content.templateId) {
    case "month_in_one_pic":
      return <OnePicCard {...props} />;
    case "monthly_vibe_receipt":
      return <ReceiptCard {...props} />;
    case "monthly_vibe_verdict":
    default:
      return <VerdictCard {...props} />;
  }
}

export function wrapVibeHeadline(
  headline: string,
  preferredLineLength: number,
): string {
  const words = headline.trim().split(/\s+/).filter(Boolean);
  if (words.length < 2) return words.join("");

  const lineCount = Math.min(
    3,
    Math.max(1, Math.ceil(headline.length / preferredLineLength)),
  );
  const lines: string[] = [];
  let index = 0;

  for (let line = 0; line < lineCount && index < words.length; line += 1) {
    const linesLeft = lineCount - line;
    const wordsLeft = words.length - index;
    const maxWords = wordsLeft - (linesLeft - 1);
    const remainingLength = words
      .slice(index)
      .reduce((sum, word) => sum + word.length, Math.max(0, wordsLeft - 1));
    const target = Math.ceil(remainingLength / linesLeft);
    let current = words[index++];

    while (index < words.length && index - (words.length - wordsLeft) < maxWords) {
      const candidate = `${current} ${words[index]}`;
      const mustLeave = words.length - (index + 1) >= linesLeft - 1;
      if (!mustLeave || candidate.length > target + 4) break;
      current = candidate;
      index += 1;
    }
    lines.push(current);
  }

  if (index < words.length) {
    lines[lines.length - 1] = `${lines[lines.length - 1]} ${words
      .slice(index)
      .join(" ")}`;
  }
  return lines.join("\n");
}

function headlineSize(headline: string, base: number, minimum: number): number {
  const longestWord = Math.max(
    ...headline.split(/\s+/).map((word) => word.length),
    1,
  );
  const byLength = headline.length > 44 ? base - 12 : headline.length > 34 ? base - 6 : base;
  const byWord = longestWord > 16 ? base - 18 : longestWord > 12 ? base - 10 : base;
  return Math.max(minimum, Math.min(byLength, byWord));
}

/* --------------------------- Template 1: Verdict -------------------------- */

const VERDICT = {
  paper: "#ECE3D1",
  ink: "#152740",
  stamp: "#B23A2E",
  muted: "#6E6353",
  frame: "#FBF8F0",
};

function VerdictCard({ content, monthText, brandLabel, t }: TemplateProps) {
  const headline = wrapVibeHeadline(content.headline, 18);
  return (
    <View style={verdictStyles.card}>
      <View style={verdictStyles.header}>
        <Text style={verdictStyles.brand}>opncue</Text>
        <Text style={verdictStyles.kicker}>
          {t("profile.monthlyVibeCard.verdict.label").toUpperCase()}
          {monthText ? `  ·  ${monthText}` : ""}
        </Text>
        <View style={verdictStyles.rule} />
      </View>

      <View style={verdictStyles.verdictFrame}>
        <View style={verdictStyles.frameRule} />
        <Text
          style={[
            verdictStyles.headline,
            { fontSize: headlineSize(content.headline, 98, 66) },
          ]}
          numberOfLines={3}
          adjustsFontSizeToFit
          minimumFontScale={0.72}
        >
          {headline}
        </Text>
        <View style={verdictStyles.frameRule} />
      </View>

      <View style={verdictStyles.copy}>
        <View style={verdictStyles.stamp}>
          <Text style={verdictStyles.stampText}>
            {t("profile.monthlyVibeCard.verdict.stamp").toUpperCase()}
          </Text>
        </View>
        <Text style={verdictStyles.subline} numberOfLines={3}>
          {content.subline}
        </Text>
      </View>

      <Text style={verdictStyles.footer}>{brandLabel}</Text>
    </View>
  );
}

const verdictStyles = StyleSheet.create({
  card: {
    width: MONTHLY_VIBE_CARD_SIZE,
    height: MONTHLY_VIBE_CARD_SIZE,
    backgroundColor: VERDICT.paper,
    padding: 72,
    justifyContent: "space-between",
  },
  header: { gap: 14 },
  brand: {
    fontFamily: Fonts.sans,
    fontSize: 46,
    fontWeight: "800",
    color: VERDICT.ink,
    textAlign: "center",
  },
  kicker: {
    fontFamily: Fonts.sans,
    fontSize: 24,
    fontWeight: "700",
    letterSpacing: 3,
    color: VERDICT.stamp,
    textAlign: "center",
  },
  rule: { height: 3, backgroundColor: VERDICT.ink, opacity: 0.5 },
  verdictFrame: {
    minHeight: 490,
    justifyContent: "center",
    gap: 30,
    marginVertical: 28,
    paddingHorizontal: 54,
    paddingVertical: 48,
    borderWidth: 14,
    borderColor: VERDICT.frame,
    backgroundColor: "rgba(251,248,240,0.34)",
  },
  frameRule: {
    alignSelf: "center",
    width: 150,
    height: 3,
    backgroundColor: VERDICT.ink,
    opacity: 0.35,
  },
  headline: {
    fontFamily: Fonts.serif,
    fontWeight: "900",
    lineHeight: 108,
    color: VERDICT.ink,
    textAlign: "center",
  },
  copy: {
    minHeight: 178,
    flexDirection: "row",
    alignItems: "center",
    gap: 36,
  },
  stamp: {
    borderWidth: 6,
    borderColor: VERDICT.stamp,
    borderRadius: 12,
    paddingHorizontal: 22,
    paddingVertical: 8,
    transform: [{ rotate: "-6deg" }],
  },
  stampText: {
    fontFamily: Fonts.sans,
    fontSize: 36,
    fontWeight: "900",
    letterSpacing: 4,
    color: VERDICT.stamp,
  },
  subline: {
    flex: 1,
    fontFamily: Fonts.sans,
    fontSize: 31,
    lineHeight: 42,
    color: VERDICT.muted,
  },
  footer: {
    fontFamily: Fonts.sans,
    fontSize: 26,
    fontWeight: "700",
    color: VERDICT.muted,
    textAlign: "center",
    letterSpacing: 1,
  },
});

/* -------------------------- Template 2: Scrapbook ------------------------- */

const ONEPIC = {
  paper: "#E9E0CB",
  ink: "#2B2A26",
  highlight: "#F2B8C6",
  tape: "rgba(170,199,220,0.72)",
  accent: "#C79A3F",
  muted: "#6B6558",
  note: "#FCFAF3",
};

function OnePicCard({ content, monthText, brandLabel, t }: TemplateProps) {
  const headline = wrapVibeHeadline(content.headline, 20);
  return (
    <View style={onePicStyles.card}>
      <View style={onePicStyles.header}>
        <Text style={onePicStyles.brand}>opncue</Text>
        <Text style={onePicStyles.title}>
          {t("profile.monthlyVibeCard.onePic.label")}
        </Text>
      </View>

      <View style={onePicStyles.noteWrap}>
        <View style={[onePicStyles.tape, onePicStyles.tapeTL]} />
        <View style={[onePicStyles.tape, onePicStyles.tapeBR]} />
        <View style={onePicStyles.note}>
          <View style={onePicStyles.highlightWrap}>
            <View style={onePicStyles.highlight} />
            <Text
              style={[
                onePicStyles.headline,
                { fontSize: headlineSize(content.headline, 92, 64) },
              ]}
              numberOfLines={3}
              adjustsFontSizeToFit
              minimumFontScale={0.72}
            >
              {headline}
            </Text>
          </View>
          <View style={onePicStyles.pencilRule} />
          <Text style={onePicStyles.subline} numberOfLines={3}>
            {content.subline}
          </Text>
        </View>
      </View>

      <View style={onePicStyles.footerRow}>
        <Text style={onePicStyles.month}>{monthText}</Text>
        <Text style={onePicStyles.footer}>{brandLabel}</Text>
      </View>
    </View>
  );
}

const onePicStyles = StyleSheet.create({
  card: {
    width: MONTHLY_VIBE_CARD_SIZE,
    height: MONTHLY_VIBE_CARD_SIZE,
    backgroundColor: ONEPIC.paper,
    padding: 76,
    justifyContent: "space-between",
  },
  header: {
    flexDirection: "row",
    alignItems: "baseline",
    justifyContent: "space-between",
  },
  brand: {
    fontFamily: Fonts.sans,
    fontSize: 44,
    fontWeight: "800",
    color: ONEPIC.ink,
  },
  title: {
    fontFamily: Fonts.serif,
    fontSize: 40,
    fontWeight: "700",
    color: ONEPIC.ink,
    fontStyle: "italic",
  },
  noteWrap: {
    alignSelf: "center",
    width: 820,
    transform: [{ rotate: "-1.5deg" }],
  },
  note: {
    minHeight: 650,
    justifyContent: "center",
    gap: 36,
    paddingHorizontal: 70,
    paddingVertical: 68,
    borderWidth: 8,
    borderColor: "rgba(43,42,38,0.08)",
    backgroundColor: ONEPIC.note,
    ...cardShadow(),
  },
  tape: {
    position: "absolute",
    width: 180,
    height: 62,
    backgroundColor: ONEPIC.tape,
    zIndex: 3,
  },
  tapeTL: { top: -24, left: 70, transform: [{ rotate: "-22deg" }] },
  tapeBR: { bottom: -18, right: 72, transform: [{ rotate: "-18deg" }] },
  highlightWrap: { alignSelf: "stretch", justifyContent: "center" },
  highlight: {
    position: "absolute",
    left: -12,
    right: -12,
    height: 64,
    bottom: 18,
    backgroundColor: ONEPIC.highlight,
    borderRadius: 6,
    transform: [{ rotate: "-1deg" }],
  },
  headline: {
    fontFamily: Fonts.serif,
    fontWeight: "800",
    lineHeight: 104,
    color: ONEPIC.ink,
    textAlign: "center",
  },
  pencilRule: {
    alignSelf: "center",
    width: 160,
    height: 4,
    backgroundColor: ONEPIC.accent,
    opacity: 0.65,
    transform: [{ rotate: "1deg" }],
  },
  subline: {
    fontFamily: Fonts.sans,
    fontSize: 36,
    lineHeight: 48,
    color: ONEPIC.muted,
    textAlign: "center",
    fontStyle: "italic",
  },
  footerRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  month: {
    fontFamily: Fonts.sans,
    fontSize: 28,
    fontWeight: "700",
    color: ONEPIC.accent,
  },
  footer: {
    fontFamily: Fonts.sans,
    fontSize: 26,
    fontWeight: "700",
    color: ONEPIC.muted,
    letterSpacing: 1,
  },
});

/* --------------------------- Template 3: Receipt -------------------------- */

const RECEIPT = {
  bg: "#FFFFFF",
  ink: "#12233F",
  pink: "#D6336C",
  faint: "#DCE3EC",
  muted: "#5B6674",
};

function ReceiptCard({ content, monthText, brandLabel, t }: TemplateProps) {
  const bars = useMemo(
    () => barcodeWidths(content.periodKey),
    [content.periodKey],
  );
  const headline = wrapVibeHeadline(content.headline, 24);
  return (
    <View style={receiptStyles.card}>
      <View style={receiptStyles.header}>
        <Text style={receiptStyles.brand}>opncue</Text>
        <Text style={receiptStyles.title}>
          {t("profile.monthlyVibeCard.receipt.label").toUpperCase()}
        </Text>
        <View style={receiptStyles.monthPill}>
          <Text style={receiptStyles.monthPillText}>{monthText}</Text>
        </View>
        <DashedRule />
      </View>

      <View style={receiptStyles.body}>
        <Text style={receiptStyles.label}>
          {t("profile.monthlyVibeCard.receipt.item").toUpperCase()}
        </Text>
        <Text
          style={[
            receiptStyles.headline,
            { fontSize: headlineSize(content.headline, 84, 62) },
          ]}
          numberOfLines={3}
          adjustsFontSizeToFit
          minimumFontScale={0.74}
        >
          {headline}
        </Text>

        <View style={receiptStyles.descriptionBlock}>
          <Text style={receiptStyles.label}>
            {t("profile.monthlyVibeCard.receipt.description").toUpperCase()}
          </Text>
          <Text style={receiptStyles.subline} numberOfLines={3}>
            {content.subline}
          </Text>
        </View>

        <View style={receiptStyles.metadataRow}>
          <View style={receiptStyles.metadataBlock}>
            <Text style={receiptStyles.label}>
              {t("profile.monthlyVibeCard.receipt.status").toUpperCase()}
            </Text>
            <Text style={receiptStyles.status}>
              {t(
                "profile.monthlyVibeCard.receipt.questionablyApproved",
              ).toUpperCase()}
            </Text>
          </View>
          <Text style={receiptStyles.accuracy}>
            {t(
              "profile.monthlyVibeCard.receipt.accuracy",
            ).toUpperCase()}
          </Text>
        </View>
      </View>

      <DashedRule />
      <View style={receiptStyles.barcode}>
        {bars.map((width, index) => (
          <View
            key={index}
            style={{
              width,
              height: 74,
              marginRight: 4,
              backgroundColor: RECEIPT.ink,
            }}
          />
        ))}
      </View>
      <View style={receiptStyles.footerRow}>
        <Text style={receiptStyles.thanks}>
          {t("profile.monthlyVibeCard.receipt.thanks")}
        </Text>
        <Text style={receiptStyles.footer}>{brandLabel}</Text>
      </View>
    </View>
  );
}

function DashedRule() {
  return <View style={receiptStyles.dashed} />;
}

function barcodeWidths(seed: string): number[] {
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = (Math.imul(31, hash) + seed.charCodeAt(index)) | 0;
  }
  const widths: number[] = [];
  for (let index = 0; index < 46; index += 1) {
    hash = (Math.imul(1103515245, hash) + 12345) | 0;
    widths.push(3 + (Math.abs(hash) % 4) * 3);
  }
  return widths;
}

const receiptStyles = StyleSheet.create({
  card: {
    width: MONTHLY_VIBE_CARD_SIZE,
    height: MONTHLY_VIBE_CARD_SIZE,
    backgroundColor: RECEIPT.bg,
    padding: 84,
    justifyContent: "space-between",
  },
  header: { alignItems: "center", gap: 16 },
  brand: {
    fontFamily: Fonts.sans,
    fontSize: 46,
    fontWeight: "800",
    color: RECEIPT.ink,
  },
  title: {
    fontFamily: Fonts.sans,
    fontSize: 34,
    fontWeight: "800",
    letterSpacing: 5,
    color: RECEIPT.ink,
  },
  monthPill: {
    borderWidth: 2,
    borderColor: RECEIPT.pink,
    borderRadius: 999,
    paddingHorizontal: 24,
    paddingVertical: 6,
  },
  monthPillText: {
    fontFamily: Fonts.sans,
    fontSize: 26,
    fontWeight: "700",
    color: RECEIPT.pink,
  },
  dashed: {
    height: 0,
    borderBottomWidth: 3,
    borderStyle: "dashed",
    borderColor: RECEIPT.faint,
    marginVertical: 8,
  },
  body: {
    flex: 1,
    justifyContent: "center",
    gap: 18,
    paddingVertical: 24,
  },
  label: {
    fontFamily: Fonts.sans,
    fontSize: 23,
    fontWeight: "800",
    letterSpacing: 3,
    color: RECEIPT.muted,
  },
  headline: {
    fontFamily: Fonts.serif,
    fontWeight: "800",
    lineHeight: 94,
    color: RECEIPT.pink,
  },
  descriptionBlock: {
    gap: 10,
    marginTop: 12,
    paddingTop: 22,
    borderTopWidth: 2,
    borderTopColor: RECEIPT.faint,
  },
  subline: {
    fontFamily: Fonts.sans,
    fontSize: 32,
    lineHeight: 43,
    color: RECEIPT.muted,
  },
  metadataRow: {
    flexDirection: "row",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 24,
    marginTop: 18,
  },
  metadataBlock: { gap: 8 },
  status: {
    fontFamily: Fonts.sans,
    fontSize: 28,
    fontWeight: "900",
    color: RECEIPT.ink,
  },
  accuracy: {
    fontFamily: Fonts.sans,
    fontSize: 20,
    fontWeight: "800",
    color: RECEIPT.pink,
    textAlign: "right",
  },
  barcode: {
    flexDirection: "row",
    alignItems: "flex-end",
    justifyContent: "center",
    paddingVertical: 4,
  },
  footerRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  thanks: {
    fontFamily: Fonts.serif,
    fontSize: 26,
    fontStyle: "italic",
    color: RECEIPT.muted,
  },
  footer: {
    fontFamily: Fonts.sans,
    fontSize: 26,
    fontWeight: "700",
    color: RECEIPT.ink,
    letterSpacing: 1,
  },
});

function cardShadow() {
  return {
    shadowColor: "#000",
    shadowOpacity: 0.12,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 6 },
  };
}
